import React, { useState, useEffect } from 'react';
import { api, useAuth } from '../App';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { CalendarIcon, Download, FileSpreadsheet, Edit2, Trash2, Filter, MapPin, ExternalLink, ShieldAlert, ShieldCheck } from 'lucide-react';
import { cn } from '../lib/utils';

export default function Attendance() {
  const { isAdmin } = useAuth();
  const [attendance, setAttendance] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(new Date());
  const [dateTo, setDateTo] = useState(new Date());
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [editForm, setEditForm] = useState({ check_in: '', check_out: '', notes: '' });

  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    fetchAttendance();
  }, [dateFrom, dateTo, selectedEmployee, selectedLocation]);

  const fetchFilters = async () => {
    try {
      const [empRes, locRes] = await Promise.all([
        api.get('/employees'),
        api.get('/locations'),
      ]);
      setEmployees(empRes.data);
      setLocations(locRes.data);
    } catch (error) {
      console.error('Failed to fetch filters');
    }
  };

  const fetchAttendance = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('date_from', format(dateFrom, 'yyyy-MM-dd'));
      params.append('date_to', format(dateTo, 'yyyy-MM-dd'));
      if (selectedEmployee !== 'all') params.append('employee_id', selectedEmployee);
      if (selectedLocation !== 'all') params.append('location_id', selectedLocation);

      const response = await api.get(`/attendance?${params.toString()}`);
      setAttendance(response.data);
    } catch (error) {
      toast.error('Failed to fetch attendance');
    } finally {
      setLoading(false);
    }
  };

  const getExportFilename = (ext) => {
    const dateRange = `${format(dateFrom, 'yyyy-MM-dd')}_to_${format(dateTo, 'yyyy-MM-dd')}`;
    if (selectedEmployee !== 'all') {
      const emp = employees.find(e => e.id === selectedEmployee);
      if (emp) return `${emp.name.replace(/ /g, '_')}_-_${dateRange}.${ext}`;
    }
    return `Attendance_-_${dateRange}.${ext}`;
  };

  const handleExportPDF = async () => {
    try {
      const params = new URLSearchParams();
      params.append('date_from', format(dateFrom, 'yyyy-MM-dd'));
      params.append('date_to', format(dateTo, 'yyyy-MM-dd'));
      if (selectedEmployee !== 'all') params.append('employee_id', selectedEmployee);
      if (selectedLocation !== 'all') params.append('location_id', selectedLocation);

      const response = await api.get(`/attendance/export/pdf?${params.toString()}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', getExportFilename('pdf'));
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF exported successfully');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  const handleExportExcel = async () => {
    try {
      const params = new URLSearchParams();
      params.append('date_from', format(dateFrom, 'yyyy-MM-dd'));
      params.append('date_to', format(dateTo, 'yyyy-MM-dd'));
      if (selectedEmployee !== 'all') params.append('employee_id', selectedEmployee);
      if (selectedLocation !== 'all') params.append('location_id', selectedLocation);

      const response = await api.get(`/attendance/export/excel?${params.toString()}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', getExportFilename('xlsx'));
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel exported successfully');
    } catch (error) {
      toast.error('Failed to export Excel');
    }
  };

  const openEditDialog = (record) => {
    setSelectedRecord(record);
    setEditForm({
      check_in: record.check_in || '',
      check_out: record.check_out || '',
      notes: record.notes || '',
    });
    setEditDialogOpen(true);
  };

  const handleEdit = async () => {
    try {
      await api.put(`/attendance/${selectedRecord.id}`, editForm);
      toast.success('Attendance updated successfully');
      setEditDialogOpen(false);
      fetchAttendance();
    } catch (error) {
      toast.error('Failed to update attendance');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/attendance/${selectedRecord.id}`);
      toast.success('Attendance deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedRecord(null);
      fetchAttendance();
    } catch (error) {
      toast.error('Failed to delete attendance');
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '-';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="attendance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-800">Attendance Records</h1>
          <p className="text-slate-500">View and manage attendance records</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleExportPDF}
            className="flex items-center gap-2"
            data-testid="export-pdf-btn"
          >
            <Download className="w-4 h-4" />
            PDF
          </Button>
          <Button
            variant="outline"
            onClick={handleExportExcel}
            className="flex items-center gap-2"
            data-testid="export-excel-btn"
          >
            <FileSpreadsheet className="w-4 h-4" />
            Excel
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Filter className="w-5 h-5 text-slate-500" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Date From */}
            <div className="space-y-2">
              <Label>From Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn("w-full justify-start text-left font-normal", !dateFrom && "text-muted-foreground")}
                    data-testid="date-from-picker"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateFrom ? format(dateFrom, "PPP") : <span>Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={dateFrom} onSelect={setDateFrom} initialFocus />
                </PopoverContent>
              </Popover>
            </div>

            {/* Date To */}
            <div className="space-y-2">
              <Label>To Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn("w-full justify-start text-left font-normal", !dateTo && "text-muted-foreground")}
                    data-testid="date-to-picker"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateTo ? format(dateTo, "PPP") : <span>Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={dateTo} onSelect={setDateTo} initialFocus />
                </PopoverContent>
              </Popover>
            </div>

            {/* Employee Filter */}
            <div className="space-y-2">
              <Label>Employee</Label>
              <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                <SelectTrigger data-testid="employee-filter">
                  <SelectValue placeholder="All Employees" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Employees</SelectItem>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Location Filter */}
            <div className="space-y-2">
              <Label>Location</Label>
              <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                <SelectTrigger data-testid="location-filter">
                  <SelectValue placeholder="All Locations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Locations</SelectItem>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardContent className="p-0">
          <Table data-testid="attendance-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold">Date</TableHead>
                <TableHead className="font-semibold">Employee</TableHead>
                <TableHead className="font-semibold">Check In</TableHead>
                <TableHead className="font-semibold">Check Out</TableHead>
                <TableHead className="font-semibold">Hours Worked</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold">Check-In Location</TableHead>
                <TableHead className="font-semibold">Check-Out Location</TableHead>
                <TableHead className="font-semibold">Range</TableHead>
                {isAdmin && <TableHead className="font-semibold text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8 text-slate-500">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : attendance.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8 text-slate-500">
                    No attendance records found
                  </TableCell>
                </TableRow>
              ) : (
                attendance.map((record) => (
                  <TableRow key={record.id} data-testid={`attendance-row-${record.id}`}>
                    <TableCell className="font-medium text-slate-800">{record.date}</TableCell>
                    <TableCell className="text-slate-600">{record.employee_name}</TableCell>
                    <TableCell className="text-slate-600">{formatTime(record.check_in)}</TableCell>
                    <TableCell className="text-slate-600">{formatTime(record.check_out)}</TableCell>
                    <TableCell className="text-slate-600">{record.hours_worked || '-'}</TableCell>
                    <TableCell>
                      {record.is_late ? (
                        <Badge variant="destructive">Late {record.late_minutes > 0 ? `${Math.floor(record.late_minutes / 60) > 0 ? Math.floor(record.late_minutes / 60) + 'h ' : ''}${record.late_minutes % 60}m` : ''}</Badge>
                      ) : (
                        <Badge className="bg-[#138808] hover:bg-[#107307]">On Time</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {record.check_in_area ? (
                        <a
                          href={`https://www.google.com/maps?q=${record.check_in_latitude},${record.check_in_longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                          data-testid={`checkin-map-link-${record.id}`}
                        >
                          <MapPin className="w-3 h-3" />
                          <span className="max-w-[140px] truncate">{record.check_in_area}</span>
                          <ExternalLink className="w-3 h-3 opacity-50" />
                        </a>
                      ) : record.location_name && record.location_name !== 'Unknown' ? (
                        <span className="text-slate-500">{record.location_name}</span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {record.check_out_area ? (
                        <a
                          href={`https://www.google.com/maps?q=${record.check_out_latitude},${record.check_out_longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                          data-testid={`checkout-map-link-${record.id}`}
                        >
                          <MapPin className="w-3 h-3" />
                          <span className="max-w-[140px] truncate">{record.check_out_area}</span>
                          <ExternalLink className="w-3 h-3 opacity-50" />
                        </a>
                      ) : record.check_out ? (
                        <span className="text-slate-400">No GPS</span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {record.check_in_out_of_range ? (
                        <Badge variant="destructive" className="text-xs flex items-center gap-1 w-fit" data-testid={`range-badge-${record.id}`}>
                          <ShieldAlert className="w-3 h-3" />
                          {record.check_in_geofence_distance}m
                        </Badge>
                      ) : record.check_in_geofence_distance != null ? (
                        <Badge className="text-xs bg-green-100 text-green-700 hover:bg-green-100 flex items-center gap-1 w-fit" data-testid={`range-badge-${record.id}`}>
                          <ShieldCheck className="w-3 h-3" />
                          {record.check_in_geofence_distance}m
                        </Badge>
                      ) : (
                        <span className="text-slate-400 text-xs">-</span>
                      )}
                    </TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(record)}
                            className="text-slate-600 hover:text-[#FF9933]"
                            data-testid={`edit-attendance-${record.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setSelectedRecord(record); setDeleteDialogOpen(true); }}
                            className="text-slate-600 hover:text-red-500"
                            data-testid={`delete-attendance-${record.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Attendance</DialogTitle>
            <DialogDescription>
              Update attendance record for {selectedRecord?.employee_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="check_in">Check In Time</Label>
                <Input
                  id="check_in"
                  type="time"
                  step="1"
                  value={editForm.check_in?.slice(0, 5) || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, check_in: e.target.value + ':00' }))}
                  data-testid="edit-check-in"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="check_out">Check Out Time</Label>
                <Input
                  id="check_out"
                  type="time"
                  step="1"
                  value={editForm.check_out?.slice(0, 5) || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, check_out: e.target.value + ':00' }))}
                  data-testid="edit-check-out"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Input
                id="notes"
                value={editForm.notes}
                onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Optional notes"
                data-testid="edit-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleEdit} className="bg-[#FF9933] hover:bg-[#E68A2E]" data-testid="save-edit-btn">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Attendance</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this attendance record? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-attendance-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
